/**
 * Mobile Bottom Navigation Menu JavaScript
 * Handles:
 * - Cart badge updates (server-rendered + JS updates)
 * - Search, Cart, and Menu action triggers
 * - Event delegation for nav items
 */

(function() {
  'use strict';

  /**
   * Click/touch targets can be a Text node (e.g. label text). Text nodes have no .closest().
   */
  function getEventTargetElement(event) {
    const t = event.target;
    if (!t) return null;
    if (t.nodeType === Node.TEXT_NODE) return t.parentElement;
    if (t.nodeType === Node.ELEMENT_NODE) return t;
    return null;
  }

  const MobileBottomMenu = {
    selectors: {
      menu: '#MobileBottomMenu',
      fab: '#MobileMenuFab',
      cartBadge: '[data-cart-count]',
      searchButton: '[data-action="search"]',
      cartButton: '[data-action="cart"]',
      menuButton: '[data-action="menu"]',
      // Existing theme selectors
      headerSearchButton: '.header__search summary, [data-search-button], details[id*="search"] summary, .search-modal summary, #SearchModal summary',
      cartDrawer: 'cart-drawer',
      cartIconBubble: '#cart-icon-bubble',
      headerMenuDrawer: 'header-drawer, menu-drawer, [data-header-drawer]',
      headerMenuButton: '[data-mm-open], .mm-hamburger, .header__icon--menu, [data-menu-button], details[id*="menu"] summary'
    },

    _docListenersBound: false,
    _cartListenersRegistered: false,
    _menuStateListenersRegistered: false,
    _menuChromeSyncFrame: null,
    _overlayStateObserver: null,
    _cartReturnFocusTarget: null,
    _cartFocusRestoreBound: false,

    isMobileViewport() {
      return window.matchMedia('(max-width: 767.98px)').matches;
    },

    /** Keep the bottom navigation in sync with every primary mobile overlay. */
    syncMenuChromeWithDrawerState() {
      /* drawer-orchestrator.js derives `theme-overlay-active` from all
         overlays. Keeping this compatibility hook avoids breaking callers
         while deliberately giving animation ownership to one controller. */
    },

    scheduleMenuChromeSync() {
      if (this._menuChromeSyncFrame) return;
      this._menuChromeSyncFrame = requestAnimationFrame(() => {
        this._menuChromeSyncFrame = null;
        this.syncMenuChromeWithDrawerState();
      });
    },

    init() {
      if (!this.isMobileViewport()) return;

      if (!this._docListenersBound) {
        this._docListenersBound = true;
        document.addEventListener('click', this.handleClick.bind(this));
        document.addEventListener('keydown', this.handleKeydown.bind(this));
      }

      if (!this._cartListenersRegistered) {
        this._cartListenersRegistered = true;
        this.initCartBadgeUpdates();
      }

      if (!this._cartFocusRestoreBound) {
        this._cartFocusRestoreBound = true;
        document.addEventListener('cart:close', () => {
          const returnTarget = this._cartReturnFocusTarget;
          this._cartReturnFocusTarget = null;
          if (!returnTarget || !returnTarget.isConnected) return;
          requestAnimationFrame(() => {
            try { returnTarget.focus({ preventScroll: true }); }
            catch (_) { returnTarget.focus(); }
          });
        });
      }

      if (!this._menuStateListenersRegistered) {
        this._menuStateListenersRegistered = true;
        this.initMenuStateListeners();
      }

      this.menu = document.querySelector(this.selectors.menu);
      if (!this.menu) return;

      this.syncMenuChromeWithDrawerState();
    },

    bindEvents() {
      // Deprecated: listeners are bound on document (see init). Kept for API compatibility.
    },

    handleClick(event) {
      const el = getEventTargetElement(event);
      if (!el || typeof el.closest !== 'function') return;

      const menu = el.closest(this.selectors.menu);
      if (!menu) return;

      let target = el.closest('[data-action]');
      if (!target && el.closest('.mobile-bottom-menu__fab-wrapper')) {
        const wrap = el.closest('.mobile-bottom-menu__fab-wrapper');
        target = wrap && wrap.querySelector('[data-action]');
      }
      if (!target) return;

      const action = target.dataset.action;
      const isFab =
        target.classList.contains('mobile-bottom-menu__fab') ||
        target.classList.contains('mobile-bottom-menu__fab-wrapper');

      this.vibrate(isFab ? 15 : 10);

      switch (action) {
        case 'search':
          event.preventDefault();
          this.openSearch();
          break;
        case 'cart':
          event.preventDefault();
          this.openCart(target);
          break;
        case 'menu':
          event.preventDefault();
          this.openMenu();
          break;
      }
    },

    handleKeydown(event) {
      const el = getEventTargetElement(event);
      if (!el || typeof el.closest !== 'function') return;
      if (!el.closest(this.selectors.menu)) return;

      const target = el.closest('[data-action]');
      if (!target) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        target.click();
      }
    },

    /**
     * Haptic feedback - vibrate on touch
     * Uses the Vibration API for supported devices
     * @param {number|number[]} pattern - Duration in ms or pattern array [vibrate, pause, vibrate, ...]
     */
    vibrate(pattern = 10) {
      if ('vibrate' in navigator) {
        try {
          navigator.vibrate(pattern);
        } catch (e) {
          // Silently fail if vibration is not allowed
        }
      }
    },

    /**
     * Open Search - Trigger existing theme search modal/drawer
     */
    openSearch() {
      // Use search bottom sheet on mobile
      if (window.searchSheet) {
        window.searchSheet.open(document.querySelector('#MobileBottomMenu [data-action="search"]'));
        return;
      }

      // Fallback: scroll to top and open header search
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader) {
        stickyHeader.preventHide = true;
        if (typeof stickyHeader.reveal === 'function') {
          stickyHeader.reveal();
        }
      }

      const headerSearchButton = document.querySelector(this.selectors.headerSearchButton);
      if (headerSearchButton) {
        setTimeout(() => {
          headerSearchButton.click();
          const input = document.querySelector('.header__search input[type="search"]');
          if (input) setTimeout(() => input.focus(), 150);
        }, 100);
        return;
      }

      const searchDetails = document.querySelector('details[id*="search"], details:has(.search-modal)');
      if (searchDetails) {
        searchDetails.setAttribute('open', '');
        const input = searchDetails.querySelector('input[type="search"], input[type="text"]');
        if (input) {
          setTimeout(() => input.focus(), 100);
        }
        return;
      }

      const predictiveSearch = document.querySelector('predictive-search');
      if (predictiveSearch) {
        const input = predictiveSearch.querySelector('input');
        if (input) {
          input.focus();
          return;
        }
      }

      window.location.href = window.routes?.search_url || '/search';
    },

    /**
     * Open Cart - Trigger existing theme cart drawer or navigate to cart page
     */
    openCart(trigger) {
      const returnTarget = trigger || document.querySelector(this.selectors.fab);
      this._cartReturnFocusTarget = returnTarget || null;
      const cartDrawer = document.querySelector(this.selectors.cartDrawer);
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        // Restore focus to the control the shopper actually used. The header
        // cart icon is hidden at this breakpoint, so using it as the return
        // target leaves focus on <body> after the drawer closes.
        if (!cartDrawer.classList.contains('active')) cartDrawer.open(returnTarget);
        else if (returnTarget && typeof cartDrawer.setActiveElement === 'function') {
          cartDrawer.setActiveElement(returnTarget);
        }
        return;
      }

      const cartIcon = document.querySelector(this.selectors.cartIconBubble);
      if (cartIcon) {
        cartIcon.click();
        return;
      }

      /* The merchant runs drawer-only checkout flow - never send the
         customer to /cart even if the drawer is genuinely missing.
         Click the cart bubble (it'll lazy-load cart-drawer.js via the
         delegate in theme.liquid) and let the in-flight loader open
         the drawer. If even that bubble isn't on the page, fall back
         to no-op rather than redirecting to /cart. */
      if (window.routes && window.routes.cart_url) {
        const fallbackTrigger = document.querySelector('a[href="' + window.routes.cart_url + '"], a[href="/cart"]');
        if (fallbackTrigger) {
          fallbackTrigger.click();
        }
      }
    },

    /**
     * Open Menu - Trigger existing theme menu drawer (mm drawer or header fallback)
     */
    openMenu() {
      const afterToggle = () => {
        requestAnimationFrame(() => {
          this.syncMenuChromeWithDrawerState();
          requestAnimationFrame(() => this.syncMenuChromeWithDrawerState());
        });
      };

      try {
        if (window.MobileMenu && typeof window.MobileMenu.toggle === 'function') {
          window.MobileMenu.toggle();
          afterToggle();
          return;
        }
      } catch (e) {
        console.warn('Mobile bottom menu: MobileMenu.toggle failed', e);
      }

      this.openMenuFallback();
    },

    /**
     * When MobileMenu is missing or failed (e.g. init skipped), open header menu UI.
     */
    openMenuFallback() {
      const mmOpenButton = document.querySelector('[data-mm-open], .mm-hamburger');
      if (mmOpenButton) {
        mmOpenButton.click();
        return;
      }

      const menuButton = document.querySelector(this.selectors.headerMenuButton);
      if (menuButton) {
        menuButton.click();
        return;
      }

      const menuDrawer = document.querySelector(this.selectors.headerMenuDrawer);
      if (menuDrawer) {
        if (typeof menuDrawer.open === 'function') {
          menuDrawer.open();
          return;
        }
        if (menuDrawer.tagName === 'DETAILS') {
          menuDrawer.setAttribute('open', '');
          return;
        }
      }

      const menuDetails = document.querySelector('header details:has(.menu-drawer), header details[id*="menu"]');
      if (menuDetails) {
        menuDetails.setAttribute('open', '');
      }
    },

    /**
     * Initialize cart badge update listeners
     */
    initCartBadgeUpdates() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.cartData && typeof event.cartData.item_count !== 'undefined') {
            this.updateCartBadge(event.cartData.item_count);
          } else {
            this.fetchAndUpdateCartBadge();
          }
        });
      }

      document.addEventListener('cart:updated', (event) => {
        if (event.detail && typeof event.detail.item_count !== 'undefined') {
          this.updateCartBadge(event.detail.item_count);
        } else {
          this.fetchAndUpdateCartBadge();
        }
      });

      document.addEventListener('cart:change', (event) => {
        if (event.detail && typeof event.detail.item_count !== 'undefined') {
          this.updateCartBadge(event.detail.item_count);
        } else {
          this.fetchAndUpdateCartBadge();
        }
      });

      this.fetchCartCountIfNeeded();
    },

    fetchAndUpdateCartBadge() {
      // Share fetch with other components via the global helper.
      const source = typeof window.getCartSnapshot === 'function'
        ? window.getCartSnapshot()
        : fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js').then(r => r.ok ? r.json() : null);
      Promise.resolve(source)
        .then(cart => {
          if (cart && typeof cart.item_count !== 'undefined') {
            this.updateCartBadge(cart.item_count);
          }
        })
        .catch(error => {
          console.warn('Mobile menu: Failed to fetch cart count', error);
        });
    },

    updateCartBadge(count) {
      const badges = document.querySelectorAll(this.selectors.cartBadge);
      const countNumber = parseInt(count, 10) || 0;

      badges.forEach(badge => {
        if (badge.closest(this.selectors.menu) || badge.closest('.mobile-bottom-menu')) {
          badge.textContent = countNumber;
          badge.classList.toggle('hidden', countNumber === 0);
        }
      });
    },

    /** Re-sync from the actual overlay classes so no component owns a separate nav state. */
    initMenuStateListeners() {
      const onChange = () => this.scheduleMenuChromeSync();
      document.addEventListener('mm:opened', onChange);
      document.addEventListener('mm:closed', onChange);

      this._overlayStateObserver = new MutationObserver(onChange);
      const observerOptions = { attributes: true, attributeFilter: ['class'] };
      this._overlayStateObserver.observe(document.documentElement, observerOptions);
      this._overlayStateObserver.observe(document.body, observerOptions);

      const cartDrawer = document.querySelector('cart-drawer');
      const searchSheet = document.getElementById('SearchSheet');
      if (cartDrawer) this._overlayStateObserver.observe(cartDrawer, observerOptions);
      if (searchSheet) this._overlayStateObserver.observe(searchSheet, observerOptions);
    },

    fetchCartCountIfNeeded() {
      const badges = document.querySelectorAll(this.selectors.cartBadge);
      let needsFetch = false;

      badges.forEach(badge => {
        if (badge.closest(this.selectors.menu) && badge.textContent.trim() === '') {
          needsFetch = true;
        }
      });

      if (!needsFetch) return;

      // Share fetch with other components via the global helper.
      const source = typeof window.getCartSnapshot === 'function'
        ? window.getCartSnapshot()
        : fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js').then(r => r.ok ? r.json() : null);
      Promise.resolve(source)
        .then(cart => {
          if (cart && typeof cart.item_count !== 'undefined') {
            this.updateCartBadge(cart.item_count);
          }
        })
        .catch(error => {
          console.warn('Mobile menu: Failed to fetch cart count', error);
        });
    }
  };

  function runInitIfMobile() {
    if (MobileBottomMenu.isMobileViewport()) {
      MobileBottomMenu.init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitIfMobile);
  } else {
    runInitIfMobile();
  }

  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      if (MobileBottomMenu.isMobileViewport()) {
        MobileBottomMenu.init();
      } else {
        const nav = document.querySelector(MobileBottomMenu.selectors.menu);
        if (nav) nav.classList.remove('menu-open', 'overlay-open');
        const stickyCart = document.getElementById('sticky-cart');
        if (stickyCart) stickyCart.classList.remove('menu-open');
      }
    }, 250);
  });

  window.addEventListener('pageshow', () => {
    MobileBottomMenu.syncMenuChromeWithDrawerState();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      MobileBottomMenu.syncMenuChromeWithDrawerState();
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (root && root.querySelector && root.querySelector('#MobileBottomMenu')) {
      MobileBottomMenu.init();
    }
  });

  window.MobileBottomMenu = MobileBottomMenu;
})();
